import { provide, onMounted, onBeforeUnmount } from 'vue'
import { state } from './state'
import { handleSearch, resetSearch } from './methods/search-handlers'
import { selectSortItem, resetSort } from './methods/sort-handlers'
import { selectFilterItem, initializeDefaultFilters, resetFilters } from './methods/filter-handlers'
import { toggleMenu, isMenuOpen, closeAllMenus, handleClickOutside } from './methods/ui-handlers'

/**
 * Composable for managing filter functionality
 * @param {Object} options - Configuration options
 * @param {Function} options.emitSearch - Function to emit search events
 * @param {Function} options.emitSort - Function to emit sort events
 * @param {Function} options.emitFilter - Function to emit filter events
 * @param {Array} options.filterOptions - Available filter options
 * @returns {Object} - Filter methods and state
 */
export default function useFilters({
  emitSearch,
  emitSort,
  emitFilter,
  filterOptions = [],
}) {
  // Provide open menu state for child components
  provide('openMenu', state.openMenu)
  
  // Setup event listeners for outside clicks
  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    
    // Initialize default filters if provided
    if (filterOptions.length > 0) {
      initializeDefaultFilters(filterOptions, emitFilter)
    }
  })
  
  // Clean up event listeners
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
  })
  
  // Methods for search functionality
  const searchMethods = {
    handleSearch: (query) => handleSearch(query, emitSearch),
    resetSearch: () => resetSearch(emitSearch),
  }
  
  // Methods for sort functionality  
  const sortMethods = {
    selectSortItem: (itemId, option) => selectSortItem(itemId, option, emitSort),
    resetSort: () => resetSort(emitSort),
  }
  
  // Methods for filter functionality
  const filterMethods = {
    selectFilterItem: (optionId, itemId) => selectFilterItem(optionId, itemId, filterOptions, emitFilter),
    resetFilters: () => resetFilters(filterOptions, emitFilter),
  }
  
  // UI control methods
  const uiMethods = {
    toggleMenu,
    isMenuOpen,
    closeAllMenus,
  }
  
  // Reset all filters, sorts, and search
  const resetAll = () => {
    resetSearch(emitSearch)
    resetSort(emitSort)
    resetFilters(filterOptions, emitFilter)
    closeAllMenus()
  }
  
  return {
    // State
    ...state,
    
    // Methods grouped by functionality
    ...searchMethods,
    ...sortMethods,
    ...filterMethods,
    ...uiMethods,
    
    // Utility methods
    resetAll,
  }
}
